import dayjs from "dayjs";

export const QUALIFIED_STATUS_DEFAULT = ["Contratado", "Entregue", "Finalizado"];
export const boletoStatus = [
  "Pendente",
  "Emitido",
  "Pago",
  "Vencido",
  "Cancelado",
];
export const FINANCIAL_STATUS_DEFAULT = [
  "pendente",
  "emitido",
  "pago",
  "vencido",
  "cancelado",
];
export const BOLETO_ACTION_VISIBLE_STATUSES = new Set(["emitido", "vencido"]);
export const FINANCIAL_STATUS_OPTIONS = boletoStatus.map((label) => ({
  value: label.toLowerCase(),
  label,
}));
export const MAX_FILTER_FETCH_ALL_PAGES = 80;

const FINANCIAL_STATUS_SET = new Set(FINANCIAL_STATUS_DEFAULT);
const INVALID_FINANCIAL_STATUS_FALLBACK = "cancelado";

function normalizeStatusKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

const FINANCIAL_STATUS_ALIASES = {
  pendente: "pendente",
  pending: "pendente",
  aberto: "pendente",
  "em aberto": "pendente",
  "a emitir": "pendente",
  "pendente boleto": "pendente",
  "pendente liberacao": "pendente",
  "pendente de liberacao": "pendente",

  emitido: "emitido",
  emitida: "emitido",
  issued: "emitido",
  enviado: "emitido",
  enviada: "emitido",
  "boleto emitido": "emitido",
  "boleto enviado": "emitido",
  "enviado boleto": "emitido",
  "pagamento solicitado": "emitido",
  "solicitado pagamento": "emitido",
  "solicitar pagamento": "emitido",

  pago: "pago",
  paga: "pago",
  paid: "pago",
  baixado: "pago",
  baixada: "pago",
  liquidado: "pago",
  liquidada: "pago",
  quitado: "pago",
  quitada: "pago",

  vencido: "vencido",
  vencida: "vencido",
  overdue: "vencido",
  atrasado: "vencido",
  atrasada: "vencido",
  "em atraso": "vencido",

  cancelado: "cancelado",
  cancelada: "cancelado",
  canceled: "cancelado",
  cancelled: "cancelado",
  bloqueado: "cancelado",
  bloqueada: "cancelado",
};

export function normalizeFinancialStatusValue(value) {
  const key = normalizeStatusKey(value);
  const normalized = FINANCIAL_STATUS_ALIASES[key] || key;
  return FINANCIAL_STATUS_SET.has(normalized) ? normalized : "";
}

export function normalizeStoredFinancialStatusValue(value, fallback = "") {
  const normalized = normalizeFinancialStatusValue(value);
  if (normalized) return normalized;

  const hasLegacyValue = String(value ?? "").trim().length > 0;
  if (hasLegacyValue) return INVALID_FINANCIAL_STATUS_FALLBACK;

  return normalizeFinancialStatusValue(fallback);
}

export function normalizeFinancialStatusValues(values, fallback = []) {
  const source = Array.isArray(values)
    ? values
    : values != null && String(values).trim()
      ? [values]
      : [];
  const normalized = Array.from(
    new Set(source.map(normalizeFinancialStatusValue).filter(Boolean)),
  );
  return normalized.length ? normalized : [...fallback];
}

export function formatFinancialStatusValue(value) {
  const normalized = normalizeFinancialStatusValue(value);
  if (!normalized) return "";
  return (
    FINANCIAL_STATUS_OPTIONS.find((option) => option.value === normalized)
      ?.label || normalized
  );
}

export function moneyToInt(value) {
  if (value == null) return 0;
  if (typeof value === "number") return Math.round(value);
  const str = String(value);
  return Math.round(Number(str.replace(/\./g, "").replace(",", ".")));
}

export function appendMoneyFilterToV2Query(q, fieldValue, queryKey) {
  if (fieldValue == null || fieldValue === "") return;
  if (typeof fieldValue === "object" && fieldValue.operator) {
    if (fieldValue.operator === "equals" && fieldValue.value !== undefined) {
      q[queryKey] = String(Number(fieldValue.value));
    }
  } else {
    const n =
      typeof fieldValue === "number" ? fieldValue : moneyToInt(fieldValue);
    q[queryKey] = String(Number(n));
  }
}

export function serializeFilterKey(dateRange, columnFilters) {
  try {
    return JSON.stringify({
      dr: Array.isArray(dateRange)
        ? dateRange.map((d) =>
            d == null ? null : formatCalendarDate(d),
          )
        : [],
      cf: columnFilters,
    });
  } catch {
    return "";
  }
}

/**
 * Interpreta datas de filtro como calendário local (YYYY-MM-DD),
 * evitando shift de fuso causado por ISO UTC (toISOString).
 */
export function parseCalendarDayjs(value) {
  if (value == null || value === "") return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    const localDate = dayjs(new Date(year, month, day, 0, 0, 0, 0));
    return localDate.isValid() ? localDate : null;
  }

  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
}

export function formatCalendarDate(value) {
  const parsed = parseCalendarDayjs(value);
  return parsed ? parsed.format("YYYY-MM-DD") : null;
}

/**
 * Converte condição salva (equals/between/before/after) em range [start, end].
 * start/end podem ser null para intervalos abertos (API dateColeta).
 */
export function buildDateRangeFromFilterCondition(operator, value) {
  if (operator === "between") {
    if (!Array.isArray(value) || value.length < 2) return null;
    const start = parseCalendarDayjs(value[0]);
    const end = parseCalendarDayjs(value[1]);
    if (!start || !end) return null;
    return [start.startOf("day"), end.startOf("day")];
  }

  const singleSource = Array.isArray(value) ? value[0] : value;
  const date = parseCalendarDayjs(singleSource);
  if (!date) return null;

  const day = date.startOf("day");

  if (operator === "before") {
    return [null, day];
  }
  if (operator === "after") {
    return [day, null];
  }

  // equals e fallback
  return [day, day];
}

export function fingerprintFreightParams(params) {
  return [
    params.uidShipper ?? "",
    params.affiliates ?? "",
    String(params.viewAll ?? ""),
    params.filterKey ?? "",
  ].join("|");
}

function arraysEqualSorted(a, b) {
  const sa = [...(a || [])].map(String).sort();
  const sb = [...(b || [])].map(String).sort();
  if (sa.length !== sb.length) return false;
  return sa.every((value, index) => value === sb[index]);
}

export function getCurrentMonthDateRange() {
  return [dayjs().startOf("month"), dayjs().endOf("month")];
}

export function buildControlPanelV2QueryParams(dateRange, columnFilters) {
  const q = {
    firstDeliveryCompleted: "true",
    financialShipper: "true",
  };

  if (dateRange?.length) {
    const [start, end] = dateRange;
    const startDate = formatCalendarDate(start);
    const endDate = formatCalendarDate(end);
    if (startDate) q.dateColetaDe = startDate;
    if (endDate) q.dateColetaAte = endDate;
  }

  if (columnFilters?.numberSerial) {
    q.numberSerial = String(columnFilters.numberSerial).trim();
  }
  if (columnFilters?.shipperName) {
    q.shipperName = String(columnFilters.shipperName).trim();
  }

  appendMoneyFilterToV2Query(q, columnFilters?.freightValue, "freightValue");

  const statusFinancialFilter = columnFilters?.statusFinancial;
  if (
    Array.isArray(statusFinancialFilter) ||
    (typeof statusFinancialFilter === "string" && statusFinancialFilter.trim())
  ) {
    const normalized = normalizeFinancialStatusValues(statusFinancialFilter);
    const isDefaultSelection = arraysEqualSorted(
      normalized,
      FINANCIAL_STATUS_DEFAULT,
    );
    // Sempre envia array: 1 ou N status usam o mesmo contrato da API
    // (evita divergência scalar vs lista no Nest/Express).
    if (!isDefaultSelection && normalized.length > 0) {
      q.statusFinancial = normalized;
    }
  }

  return q;
}

export function isFinancialStatusFilterNonDefault(columnFilters) {
  const statusFinancialFilter = columnFilters?.statusFinancial;
  if (
    Array.isArray(statusFinancialFilter) ||
    (typeof statusFinancialFilter === "string" && statusFinancialFilter.trim())
  ) {
    const normalized = normalizeFinancialStatusValues(statusFinancialFilter);
    return (
      normalized.length > 0 &&
      !arraysEqualSorted(normalized, FINANCIAL_STATUS_DEFAULT)
    );
  }
  return false;
}

/**
 * Draft inicial do filtro de status: só valores existentes nos dados.
 * Seleção "padrão" (sem filtro) marca todos os status presentes, não a lista canônica.
 */
export function buildStatusFilterDraft(appliedValues, availableValues) {
  const available = Array.from(availableValues || []).filter(Boolean);
  const availableSet = new Set(available);
  const normalizedApplied = normalizeFinancialStatusValues(
    appliedValues,
    FINANCIAL_STATUS_DEFAULT,
  );

  if (
    !normalizedApplied.length ||
    arraysEqualSorted(normalizedApplied, FINANCIAL_STATUS_DEFAULT)
  ) {
    return available;
  }

  return normalizedApplied.filter((status) => availableSet.has(status));
}

/**
 * Persiste o filtro aplicado. Se nada / todos os disponíveis estiverem
 * selecionados, grava o default canônico (sem filtro no servidor).
 */
export function resolveStatusFilterForApply(draftValues, availableValues) {
  const available = Array.from(availableValues || []).filter(Boolean);
  const availableSet = new Set(available);
  const normalized = Array.from(
    new Set(
      (Array.isArray(draftValues) ? draftValues : [])
        .map(normalizeFinancialStatusValue)
        .filter((status) => status && availableSet.has(status)),
    ),
  );

  if (!normalized.length) {
    return [...FINANCIAL_STATUS_DEFAULT];
  }

  const allAvailableSelected =
    available.length > 0 &&
    available.every((status) => normalized.includes(status)) &&
    normalized.every((status) => availableSet.has(status));

  if (allAvailableSelected) {
    return [...FINANCIAL_STATUS_DEFAULT];
  }

  return normalized;
}

export function matchesDateInRange(value, range) {
  if (!Array.isArray(range) || range.length === 0) return true;

  const hasStart = range[0] != null && range[0] !== "";
  const hasEnd = range[1] != null && range[1] !== "";
  if (!hasStart && !hasEnd) return true;
  if (value == null || value === "") return false;

  const date = parseCalendarDayjs(value);
  if (!date || !date.isValid()) return false;

  if (hasStart) {
    const start = parseCalendarDayjs(range[0])?.startOf("day");
    if (start && date.isBefore(start)) return false;
  }

  if (hasEnd) {
    const end = parseCalendarDayjs(range[1])?.endOf("day");
    if (end && date.isAfter(end)) return false;
  }

  return true;
}

export function getRowFinancialStatus(row) {
  return normalizeFinancialStatusValue(
    row?.statusFinancial ?? row?.financialStatus,
  );
}

export function applyClientFinancialStatusFilter(rows, columnFilters) {
  if (!isFinancialStatusFilterNonDefault(columnFilters)) {
    return rows || [];
  }

  const allowed = new Set(
    normalizeFinancialStatusValues(columnFilters?.statusFinancial),
  );
  if (!allowed.size) return rows || [];

  return (rows || []).filter((row) => {
    const status = getRowFinancialStatus(row);
    return Boolean(status) && allowed.has(status);
  });
}

export function applyClientDateFilters(rows, columnDateRanges = {}) {
  let result = rows || [];

  const issueRange = columnDateRanges?.issueDate;
  if (
    Array.isArray(issueRange) &&
    issueRange.length === 2 &&
    (issueRange[0] || issueRange[1])
  ) {
    result = result.filter((row) =>
      matchesDateInRange(row?.issueDate, issueRange),
    );
  }

  const dueRange = columnDateRanges?.dueDate;
  if (
    Array.isArray(dueRange) &&
    dueRange.length === 2 &&
    (dueRange[0] || dueRange[1])
  ) {
    result = result.filter((row) => {
      if (row?.installmentNumber != null) {
        return matchesDateInRange(row?.dueDate, dueRange);
      }

      const installments = row?.dueDateInstallments || [];
      const dates =
        installments.length > 1 ? installments : [row?.dueDate].filter(Boolean);
      if (!dates.length) return false;
      return dates.some((date) => matchesDateInRange(date, dueRange));
    });
  }

  return result;
}

export function collectFinancialStatusesFromRows(rows) {
  const values = new Set();
  (rows || []).forEach((row) => {
    const normalized = getRowFinancialStatus(row);
    if (normalized) values.add(normalized);
  });
  return values;
}

export function hasClientDateFilters(columnDateRanges) {
  const issue = columnDateRanges?.issueDate;
  const due = columnDateRanges?.dueDate;
  return (
    (Array.isArray(issue) &&
      issue.length === 2 &&
      (issue[0] != null || issue[1] != null)) ||
    (Array.isArray(due) &&
      due.length === 2 &&
      (due[0] != null || due[1] != null))
  );
}

export function parseStoredDateRange(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== 2) return [];
    const start = parseCalendarDayjs(parsed[0]);
    const end = parseCalendarDayjs(parsed[1]);
    if (!start || !end) return [];
    return [start.startOf("day"), end.startOf("day")];
  } catch {
    return [];
  }
}

export function serializeDateRangeForStorage(range) {
  if (!Array.isArray(range) || range.length !== 2) return null;
  if (range[0] == null && range[1] == null) return null;

  const start = formatCalendarDate(range[0]);
  const end = formatCalendarDate(range[1]);
  if (!start && !end) return null;

  // Persistência do filtro de coluna usa RangePicker (exige início e fim).
  if (!start || !end) return null;
  return JSON.stringify([start, end]);
}

export function hasActiveControlPanelFilters(
  dateRange,
  columnFilters,
  columnDateRanges,
) {
  if (dateRange?.length) return true;
  if (hasClientDateFilters(columnDateRanges)) return true;
  if (columnFilters?.numberSerial) {
    const value = String(columnFilters.numberSerial).trim();
    if (value) return true;
  }
  if (columnFilters?.shipperName) {
    const value = String(columnFilters.shipperName).trim();
    if (value) return true;
  }
  if (
    columnFilters?.freightValue != null &&
    columnFilters?.freightValue !== ""
  ) {
    return true;
  }

  return isFinancialStatusFilterNonDefault(columnFilters);
}

export function mergeFreightRowsById(rows) {
  const byId = new Map();
  (rows || []).forEach((row) => {
    if (!row?.id) return;
    const prev = byId.get(row.id);
    byId.set(row.id, prev ? { ...prev, ...row } : row);
  });
  return Array.from(byId.values());
}

export function hasBoletoAttachmentOnRecord(record) {
  const attachment = record?.boletoAttachment;
  if (attachment == null || attachment === "") return false;

  if (typeof attachment === "string") {
    return attachment.trim().length > 0;
  }

  if (typeof attachment === "object") {
    return Boolean(
      String(attachment.url || attachment.downloadUrl || "").trim() ||
        String(
          attachment.name || attachment.originalName || attachment.fileName || "",
        ).trim(),
    );
  }

  return false;
}

export function canShowFreightControlBoletoAction(record) {
  const status = normalizeFinancialStatusValue(
    record?.statusFinancial ?? record?.financialStatus,
  );

  if (!BOLETO_ACTION_VISIBLE_STATUSES.has(status)) {
    return false;
  }

  return hasBoletoAttachmentOnRecord(record);
}

export function freightControlStorageKey(uid, key) {
  return `${uid}:freightControl:${key}`;
}

export function resetCursorMapFromPage(cursorMapRef, startPage) {
  if (!Number.isFinite(startPage)) return;
  const nextMap = new Map();
  cursorMapRef.current.forEach((value, key) => {
    if (key < startPage) nextMap.set(key, value);
  });
  cursorMapRef.current = nextMap;
}
